import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { formatDate, longDateFormat } from 'utils/date';

interface IUserTableCard {
  data: ITableDataSource;
  activateUser: (id: string, active: boolean) => void;
  editUser: (id: string) => void;
  unbanUser: (id: string) => void;
  loggedUserId: string;
}

const UserTableCard = ({
  data,
  activateUser,
  editUser,
  unbanUser,
  loggedUserId,
}: IUserTableCard) => (
  <Grid
    container
    sx={{
      border: `1px solid ${colors.gray200}`,
      borderRadius: '4px',
      overflow: 'hidden',
      mt: 1,
      backgroundColor: colors.gray100,
    }}
  >
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        Email
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all', 'overflow-wrap': 'anywhere' }}>
        {data.email}
      </Typography>
    </Grid>
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        First Name
      </Typography>
      <Typography variant="body2">{data.firstName}</Typography>
    </Grid>
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        Last Name
      </Typography>
      <Typography variant="body2">{data.lastName}</Typography>
    </Grid>
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        Phone Number
      </Typography>
      <Typography variant="body2">{data.phoneNumber}</Typography>
    </Grid>
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        Status
      </Typography>

      {data.blocked ? (
        <Chips text="Blocked" variant={EChipsVariants.warning} />
      ) : (
        <Chips
          text={data.status ? 'Inactive' : 'Active'}
          variant={data.status ? EChipsVariants.error : EChipsVariants.success100}
        />
      )}
    </Grid>
    <Grid
      size={{
        xs: 6,
        lg: 3,
      }}
      sx={{
        p: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        Last Login
      </Typography>
      <Typography variant="body2">{formatDate(data.lastLoginDate, longDateFormat)}</Typography>
    </Grid>
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: {
          xs: `1px solid ${colors.primary25}`,
          lg: 'none',
        },
        m: {
          xs: 2,
          lg: 2,
        },
        pt: 1,
      }}
    >
      <Grid size={{ xs: 12, sm: 'auto' }}>
        <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
          Actions
        </Typography>
      </Grid>
      <Grid
        container
        sx={{ mt: 1, rowGap: 1, justifyContent: 'right' }}
        size={{ xs: 12, sm: 'auto' }}
      >
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ pr: { sm: 2, lg: 0 } }}>
          <Button
            label="Edit"
            variant={EButtonVariants.outlined}
            onClick={() => editUser(data.id as string)}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: '120px' } }}
          />
        </Grid>
        {loggedUserId !== data.id && (
          <Grid size={{ xs: 12, sm: 'auto' }}>
            {data.blocked ? (
              <Button
                label="Unblock"
                variant={EButtonVariants.outlined}
                onClick={() => unbanUser(data.id as string)}
                size="small"
                fullWidth
                sx={{ minWidth: { sm: '120px' } }}
              />
            ) : (
              <Button
                label={data.active ? 'Deactivate' : 'Activate'}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  data.active
                    ? activateUser(data.id as string, false)
                    : activateUser(data.id as string, true)
                }
                size="small"
                fullWidth
                sx={{ minWidth: { sm: '120px' } }}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  </Grid>
);

export { UserTableCard };
